import { encryptToken, decryptToken, isEncrypted } from './tokenEncryption';

/**
 * Secure Token Storage
 *
 * Security modes:
 * - 'memory': Tokens stored in memory only (most secure, lost on refresh)
 * - 'encrypted': Tokens encrypted in sessionStorage (good balance)
 * - 'standard': Plain sessionStorage (legacy, least secure)
 *
 * For maximum XSS protection, use 'memory' mode with HttpOnly refresh cookies
 */
export class AppStorage {
    constructor(mode = 'encrypted') {
        this.mode = mode;
        this.storage = window.sessionStorage;
        this.tokenName = "ctoken";
        this.refreshTokenName = "refresh";

        // In-memory storage (not accessible via storage inspection)
        this._memoryTokens = {
            access: null,
            refresh: null
        };

        // Cache for encrypted mode
        this._accessTokenCache = null;
        this._refreshTokenCache = null;
    }

    /**
     * Set storage mode
     * @param {'memory' | 'encrypted' | 'standard'} mode
     */
    setMode(mode) {
        this.mode = mode;
    }

    /**
     * Get access token based on storage mode
     */
    getAccessToken(){
        // Memory mode - most secure against XSS
        if (this.mode === 'memory') {
            return this._memoryTokens.access;
        }

        // Return cached decrypted value if available
        if (this._accessTokenCache) {
            return this._accessTokenCache;
        }

        const stored = this.storage.getItem(this.tokenName);
        if (!stored) return null;

        // Standard mode - return as-is
        if (this.mode === 'standard' || !isEncrypted(stored)) {
            return stored;
        }

        // Encrypted mode - trigger async decryption
        this.getAccessTokenAsync().then(token => {
            this._accessTokenCache = token;
        });

        return this._accessTokenCache || stored;
    }

    /**
     * Get access token with async decryption
     */
    async getAccessTokenAsync(){
        if (this.mode === 'memory') {
            return this._memoryTokens.access;
        }

        const stored = this.storage.getItem(this.tokenName);
        if (!stored) return null;

        if (this.mode === 'standard' || !isEncrypted(stored)) {
            return stored;
        }

        const decrypted = await decryptToken(stored);
        this._accessTokenCache = decrypted;
        return decrypted;
    }

    /**
     * Set access token based on storage mode
     */
    async setAccessToken(value){
        if (!value) {
            this.resetAccessToken();
            return;
        }

        // Memory mode - store only in memory (XSS protected)
        if (this.mode === 'memory') {
            this._memoryTokens.access = value;
            return;
        }

        this._accessTokenCache = value;

        // Standard mode - plain storage
        if (this.mode === 'standard') {
            this.storage.setItem(this.tokenName, value);
            return;
        }

        // Encrypted mode
        try {
            const encrypted = await encryptToken(value);
            this.storage.setItem(this.tokenName, encrypted);
        } catch (error) {
            console.warn('Token encryption failed, storing plain:', error);
            this.storage.setItem(this.tokenName, value);
        }
    }

    /**
     * Get refresh token based on storage mode
     */
    getRefreshToken(){
        if (this.mode === 'memory') {
            return this._memoryTokens.refresh;
        }

        if (this._refreshTokenCache) {
            return this._refreshTokenCache;
        }

        const stored = this.storage.getItem(this.refreshTokenName);
        if (!stored) return null;

        if (this.mode === 'standard' || !isEncrypted(stored)) {
            return stored;
        }

        this.getRefreshTokenAsync().then(token => {
            this._refreshTokenCache = token;
        });

        return this._refreshTokenCache || stored;
    }

    /**
     * Get refresh token with async decryption
     */
    async getRefreshTokenAsync(){
        if (this.mode === 'memory') {
            return this._memoryTokens.refresh;
        }

        const stored = this.storage.getItem(this.refreshTokenName);
        if (!stored) return null;

        if (this.mode === 'standard' || !isEncrypted(stored)) {
            return stored;
        }

        const decrypted = await decryptToken(stored);
        this._refreshTokenCache = decrypted;
        return decrypted;
    }

    /**
     * Set refresh token based on storage mode
     */
    async setRefreshToken(value){
        if (!value) {
            this.resetRefreshToken();
            return;
        }

        if (this.mode === 'memory') {
            this._memoryTokens.refresh = value;
            return;
        }

        this._refreshTokenCache = value;

        if (this.mode === 'standard') {
            this.storage.setItem(this.refreshTokenName, value);
            return;
        }

        try {
            const encrypted = await encryptToken(value);
            this.storage.setItem(this.refreshTokenName, encrypted);
        } catch (error) {
            console.warn('Token encryption failed, storing plain:', error);
            this.storage.setItem(this.refreshTokenName, value);
        }
    }

    resetAccessToken(){
        this._memoryTokens.access = null;
        this._accessTokenCache = null;
        this.storage.removeItem(this.tokenName);
    }

    resetRefreshToken(){
        this._memoryTokens.refresh = null;
        this._refreshTokenCache = null;
        this.storage.removeItem(this.refreshTokenName);
    }

    /**
     * Clear all tokens
     */
    clearAll(){
        this.resetAccessToken();
        this.resetRefreshToken();
    }
}
